import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import {
  Megaphone,
  FileText,
  Share2,
  Users,
  DollarSign,
  Eye,
  ArrowUpRight,
  ArrowDownRight,
  TrendingUp,
  Zap,
  Search,
  Mail,
  BarChart3,
  Target,
  Clock,
  Plus,
  Layers,
  CheckCircle2,
  Circle,
  ArrowRight,
  Activity,
  Globe,
  ShieldCheck,
  ShieldAlert,
  Sparkles,
  Gauge,
  AlertTriangle,
  CircleDollarSign,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

type DashboardOverview = {
  campaigns?: {
    total?: number;
    active?: number;
    draft?: number;
    paused?: number;
    completed?: number;
    totalBudget?: number;
    totalSpent?: number;
    trend?: number;
  };
  content?: {
    total?: number;
    published?: number;
    draft?: number;
    review?: number;
    trend?: number;
  };
  platforms?: number;
  email?: {
    subscribers?: number;
    campaigns?: number;
    sent?: number;
    avgOpenRate?: number;
    avgClickRate?: number;
    totalSent?: number;
    totalOpened?: number;
    totalClicked?: number;
    subscriberTrend?: number;
  };
  ads?: {
    active?: number;
    total?: number;
    totalSpent?: number;
    impressions?: number;
    clicks?: number;
    conversions?: number;
    revenue?: number;
  };
  leads?: {
    new?: number;
    trend?: number;
  };
  automations?: {
    active?: number;
    total?: number;
    runs30d?: number;
    successRate?: number;
  };
  seo?: {
    projects?: number;
  };
  scheduledPosts?: number;
};

type PerformanceData = {
  period?: string;
  daily?: Array<Record<string, number | string>>;
  byPlatform?: Record<string, number>;
  adDaily?: Array<{
    date: string;
    impressions: number;
    clicks: number;
    conversions: number;
    spent: number;
  }>;
};

type CampaignSummary = {
  id: string;
  name: string;
  type?: string;
  status: string;
  budget: number;
  spent: number;
  budgetPct: number;
  timelinePct: number;
  tags?: string;
  endDate?: string;
  _count?: {
    contents?: number;
    scheduledPosts?: number;
    milestones?: number;
  };
};

type ContentPipeline = {
  pipeline?: Record<string, number>;
  types?: Record<string, number>;
  recent?: Array<{
    id: string;
    title: string;
    type: string;
    status: string;
    createdBy?: { name?: string };
  }>;
};

type DashboardActivity = {
  items?: Array<{
    id: string;
    module: string;
    title: string;
    detail: string;
    status: string;
    timestamp: string;
  }>;
};

type ModuleHealthItem = {
  name: string;
  status: string;
  count: number;
  path: string;
};

type InsightItem = {
  severity: "critical" | "high" | "moderate" | "info";
  title: string;
  detail: string;
  actionLabel: string;
  actionPath: string;
};

const GRADIENT = "bg-gradient-to-r from-violet-500 via-purple-500 to-pink-500";
const PIE_COLORS = ["#8B5CF6", "#A855F7", "#EC4899", "#3B82F6", "#10B981", "#F59E0B", "#EF4444"];

const STATUS_COLORS: Record<string, string> = {
  draft: "bg-gray-100 text-gray-700",
  review: "bg-yellow-100 text-yellow-700",
  approved: "bg-blue-100 text-blue-700",
  published: "bg-green-100 text-green-700",
  active: "bg-green-100 text-green-700",
  paused: "bg-yellow-100 text-yellow-700",
  completed: "bg-purple-100 text-purple-700",
  archived: "bg-gray-100 text-gray-500",
  new: "bg-blue-100 text-blue-700",
  sent: "bg-green-100 text-green-700",
  success: "bg-green-100 text-green-700",
  failure: "bg-red-100 text-red-700",
  running: "bg-blue-100 text-blue-700",
  connected: "bg-green-100 text-green-700",
  disconnected: "bg-red-100 text-red-700",
  idle: "bg-gray-100 text-gray-600",
};

const MODULE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  content: FileText,
  campaign: Megaphone,
  lead: Users,
  email: Mail,
  automation: Zap,
  social: Globe,
  ad: DollarSign,
  seo: Search,
};

const MODULE_COLORS: Record<string, string> = {
  content: "text-green-600 bg-green-50",
  campaign: "text-blue-600 bg-blue-50",
  lead: "text-amber-600 bg-amber-50",
  email: "text-purple-600 bg-purple-50",
  automation: "text-yellow-600 bg-yellow-50",
  social: "text-pink-600 bg-pink-50",
  ad: "text-red-500 bg-red-50",
  seo: "text-cyan-600 bg-cyan-50",
};

const LEGACY_ROUTE_MAP: Record<string, string> = {
  "/content-studio": "/content",
  "/social-hub": "/social",
  "/email-marketing": "/email",
  "/ad-manager": "/ads",
  "/seo-tools": "/seo",
};

function normalizePath(path?: string) {
  if (!path) return "/";
  return LEGACY_ROUTE_MAP[path] || path;
}

function formatMoney(value: number) {
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function pct(numerator: number, denominator: number) {
  if (!denominator) return 0;
  return Math.round((numerator / denominator) * 100);
}

function clamp(value: number, min = 0, max = 100) {
  return Math.min(max, Math.max(min, value));
}

function severityStyle(severity: InsightItem["severity"]) {
  switch (severity) {
    case "critical":
      return "border-red-200 bg-red-50 text-red-700";
    case "high":
      return "border-orange-200 bg-orange-50 text-orange-700";
    case "moderate":
      return "border-amber-200 bg-amber-50 text-amber-700";
    default:
      return "border-blue-200 bg-blue-50 text-blue-700";
  }
}

function severityLabel(severity: InsightItem["severity"]) {
  switch (severity) {
    case "critical":
      return "Critical";
    case "high":
      return "High";
    case "moderate":
      return "Moderate";
    default:
      return "Info";
  }
}

function KPICard({
  label,
  value,
  icon: Icon,
  trend,
  color,
  sub,
}: {
  label: string;
  value: number | string;
  icon: React.ComponentType<{ className?: string }>;
  trend?: number;
  color: string;
  sub?: string;
}) {
  return (
    <div className="card p-5 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        {trend !== undefined && trend !== 0 && (
          <span
            className={`flex items-center gap-0.5 text-xs font-semibold px-2 py-0.5 rounded-full ${
              trend > 0 ? "bg-green-50 text-green-600" : "bg-red-50 text-red-600"
            }`}
          >
            {trend > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {Math.abs(trend)}%
          </span>
        )}
      </div>
      <p className="text-2xl font-bold text-gray-900">{typeof value === "number" ? value.toLocaleString() : value}</p>
      <p className="text-sm text-gray-500 mt-1">{label}</p>
      {sub ? <p className="text-xs text-gray-400 mt-0.5">{sub}</p> : null}
    </div>
  );
}

function GoalRow({ title, current, target, helper }: { title: string; current: number; target: number; helper: string }) {
  const progress = clamp(pct(current, target));
  const color = progress >= 100 ? "bg-green-500" : progress >= 70 ? "bg-purple-500" : "bg-amber-500";

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-gray-800">{title}</span>
        <span className="text-xs text-gray-500">
          {current.toLocaleString()} / {target.toLocaleString()}
        </span>
      </div>
      <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${progress}%` }} />
      </div>
      <p className="text-xs text-gray-400">{helper}</p>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [performanceWindow, setPerformanceWindow] = useState<7 | 30 | 90>(30);

  const { data: overview, isLoading } = useQuery({
    queryKey: ["dashboard-overview"],
    queryFn: () => api.get<DashboardOverview>("/dashboard/overview"),
  });

  const { data: performance } = useQuery({
    queryKey: ["dashboard-performance", performanceWindow],
    queryFn: () => api.get<PerformanceData>(`/dashboard/performance?days=${performanceWindow}`),
  });

  const { data: activity } = useQuery({
    queryKey: ["dashboard-activity"],
    queryFn: () => api.get<DashboardActivity>("/dashboard/activity"),
  });

  const { data: campaignsSummary } = useQuery({
    queryKey: ["dashboard-campaigns-summary"],
    queryFn: () => api.get<CampaignSummary[]>("/dashboard/campaigns-summary"),
  });

  const { data: contentPipeline } = useQuery({
    queryKey: ["dashboard-content-pipeline"],
    queryFn: () => api.get<ContentPipeline>("/dashboard/content-pipeline"),
  });

  const { data: moduleHealth } = useQuery({
    queryKey: ["dashboard-module-health"],
    queryFn: () => api.get<ModuleHealthItem[]>("/dashboard/module-health"),
  });

  const o = overview || {};

  const adChartData = performance?.adDaily?.length
    ? performance.adDaily.map((d) => ({
        date: d.date.slice(5),
        impressions: d.impressions,
        clicks: d.clicks,
        conversions: d.conversions,
      }))
    : performance?.daily?.length
      ? performance.daily.map((d) => ({
          date: String(d.date || "").slice(5),
          impressions: Number(d.impression || 0),
          clicks: Number(d.click || 0),
          conversions: Number(d.conversion || 0),
        }))
      : [];

  const platformData = performance?.byPlatform
    ? Object.entries(performance.byPlatform).map(([name, value]) => ({ name, value }))
    : [];

  const pipelineStages = ["draft", "review", "approved", "published"];
  const pipelineData = pipelineStages.map((stage) => ({
    stage: stage.charAt(0).toUpperCase() + stage.slice(1),
    count: contentPipeline?.pipeline?.[stage] || 0,
  }));

  const contentTypeData = contentPipeline?.types
    ? Object.entries(contentPipeline.types).map(([name, count]) => ({ name, count }))
    : [];

  const normalizedModuleHealth = useMemo(
    () =>
      (moduleHealth || []).map((item) => ({
        ...item,
        path: normalizePath(item.path),
      })),
    [moduleHealth]
  );

  const enterpriseMetrics = useMemo(() => {
    const adsSpent = o.ads?.totalSpent || 0;
    const adsRevenue = o.ads?.revenue || 0;
    const adsClicks = o.ads?.clicks || 0;
    const adsImpressions = o.ads?.impressions || 0;
    const adsConversions = o.ads?.conversions || 0;

    const roi = adsSpent > 0 ? Math.round(((adsRevenue - adsSpent) / adsSpent) * 100) : 0;
    const ctr = adsImpressions > 0 ? Number(((adsClicks / adsImpressions) * 100).toFixed(2)) : 0;
    const conversionRate = adsClicks > 0 ? Number(((adsConversions / adsClicks) * 100).toFixed(2)) : 0;

    const publishRatio = pct(o.content?.published || 0, o.content?.total || 0);
    const budgetUtilization = pct(o.campaigns?.totalSpent || 0, o.campaigns?.totalBudget || 0);

    let score = 0;
    score += Math.min(25, (o.campaigns?.active || 0) * 5);
    score += Math.min(20, publishRatio * 0.2);
    score += Math.min(15, (o.platforms || 0) * 3);
    score += Math.min(20, o.automations?.successRate || 0);
    score += Math.min(20, o.email?.avgOpenRate || 0);

    return {
      score: clamp(Math.round(score)),
      roi,
      ctr,
      conversionRate,
      publishRatio,
      budgetUtilization,
      adsSpent,
      adsRevenue,
    };
  }, [o]);

  const insights = useMemo<InsightItem[]>(() => {
    const list: InsightItem[] = [];

    if ((o.platforms || 0) < 2) {
      list.push({
        severity: "high",
        title: "Platform coverage is low",
        detail: "Connect more social channels to expand audience reach and stabilize acquisition costs.",
        actionLabel: "Open Social Hub",
        actionPath: "/social",
      });
    }

    if (enterpriseMetrics.budgetUtilization > 85) {
      list.push({
        severity: "critical",
        title: "Campaign budgets nearing cap",
        detail: `${enterpriseMetrics.budgetUtilization}% of allocated budget is already spent. Rebalance active campaigns now.`,
        actionLabel: "Review Campaigns",
        actionPath: "/campaigns",
      });
    }

    if ((o.email?.avgOpenRate || 0) < 18) {
      list.push({
        severity: "moderate",
        title: "Email open rate below enterprise benchmark",
        detail: "Current open rate trails modern lifecycle standards. Optimize subject lines and send-time windows.",
        actionLabel: "Tune Email Ops",
        actionPath: "/email",
      });
    }

    if ((o.automations?.successRate || 0) < 90) {
      list.push({
        severity: "high",
        title: "Automation reliability needs hardening",
        detail: "Success rate is below 90%. Audit failed runs and add retries/guardrails to mission-critical flows.",
        actionLabel: "Open Automations",
        actionPath: "/automations",
      });
    }

    if (!list.length) {
      list.push({
        severity: "info",
        title: "Operational posture is healthy",
        detail: "Your active modules are stable. Next growth lever is scaling conversion campaigns and cross-channel attribution.",
        actionLabel: "View Analytics",
        actionPath: "/analytics",
      });
    }

    return list.slice(0, 4);
  }, [enterpriseMetrics.budgetUtilization, o]);

  const riskCampaigns = useMemo(() => {
    return (campaignsSummary || []).filter((campaign) => campaign.budgetPct >= 85).slice(0, 4);
  }, [campaignsSummary]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
      </div>
    );
  }

  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">{today} · Enterprise Marketing Overview</p>
        </div>
        <div className="flex items-center gap-2">
          {[
            { label: "Campaign", path: "/campaigns" },
            { label: "Content", path: "/content" },
            { label: "Email", path: "/email" },
          ].map((action) => (
            <button
              key={action.label}
              onClick={() => navigate(action.path)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> {action.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card p-5 bg-gradient-to-br from-indigo-50 via-white to-purple-50">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-indigo-600" /> Enterprise Readiness
            </h3>
            <span className="text-xs font-semibold px-2 py-1 rounded-full bg-indigo-100 text-indigo-700">Live</span>
          </div>
          <div className="flex items-end gap-3">
            <p className="text-3xl font-bold text-gray-900">{enterpriseMetrics.score}</p>
            <p className="text-sm text-gray-500 pb-1">/ 100 score</p>
          </div>
          <div className="mt-3 h-2 w-full rounded-full bg-gray-100 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-purple-500" style={{ width: `${enterpriseMetrics.score}%` }} />
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Weighted by campaign activity, publishing velocity, platform coverage, automation reliability, and email engagement.
          </p>
        </div>

        <div className="card p-5">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <CircleDollarSign className="w-4 h-4 text-green-600" /> Revenue Efficiency
          </h3>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-500">ROI</p>
              <p className={`text-2xl font-bold ${enterpriseMetrics.roi >= 0 ? "text-green-600" : "text-red-600"}`}>
                {enterpriseMetrics.roi}%
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Spend</p>
              <p className="text-2xl font-bold text-gray-900">{formatMoney(enterpriseMetrics.adsSpent)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Attributed Revenue</p>
              <p className="text-sm font-semibold text-gray-800">{formatMoney(enterpriseMetrics.adsRevenue)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Conversion Rate</p>
              <p className="text-sm font-semibold text-gray-800">{enterpriseMetrics.conversionRate}%</p>
            </div>
          </div>
        </div>

        <div className="card p-5">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <Gauge className="w-4 h-4 text-amber-600" /> Operational Pressure
          </h3>
          <div className="mt-4 space-y-3">
            <div>
              <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                <span>Budget Utilization</span>
                <span>{enterpriseMetrics.budgetUtilization}%</span>
              </div>
              <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
                <div
                  className={`h-full rounded-full ${
                    enterpriseMetrics.budgetUtilization > 85
                      ? "bg-red-500"
                      : enterpriseMetrics.budgetUtilization > 70
                        ? "bg-amber-500"
                        : "bg-green-500"
                  }`}
                  style={{ width: `${clamp(enterpriseMetrics.budgetUtilization)}%` }}
                />
              </div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">CTR</span>
              <span className="font-semibold text-gray-800">{enterpriseMetrics.ctr}%</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Published Content Ratio</span>
              <span className="font-semibold text-gray-800">{enterpriseMetrics.publishRatio}%</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          label="Active Campaigns"
          value={o.campaigns?.active || 0}
          icon={Megaphone}
          color="bg-blue-600"
          trend={o.campaigns?.trend}
          sub={`${o.campaigns?.total || 0} total · $${((o.campaigns?.totalBudget || 0) / 1000).toFixed(0)}K budget`}
        />
        <KPICard
          label="Total Content"
          value={o.content?.total || 0}
          icon={FileText}
          color="bg-green-600"
          trend={o.content?.trend}
          sub={`${o.content?.published || 0} published · ${o.content?.draft || 0} drafts`}
        />
        <KPICard
          label="Connected Platforms"
          value={o.platforms || 0}
          icon={Share2}
          color="bg-purple-600"
          sub={`${o.scheduledPosts || 0} scheduled posts`}
        />
        <KPICard
          label="Email Subscribers"
          value={o.email?.subscribers || 0}
          icon={Users}
          color="bg-amber-600"
          trend={o.email?.subscriberTrend}
          sub={`${o.email?.avgOpenRate || 0}% avg open rate`}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          label="Total Ad Spend"
          value={formatMoney(o.ads?.totalSpent || 0)}
          icon={DollarSign}
          color="bg-red-500"
          sub={`${o.ads?.total || 0} campaigns · ${o.ads?.active || 0} active`}
        />
        <KPICard
          label="Ad Impressions"
          value={o.ads?.impressions || 0}
          icon={Eye}
          color="bg-cyan-600"
          sub={`${o.ads?.clicks || 0} clicks · ${enterpriseMetrics.ctr}% CTR`}
        />
        <KPICard
          label="New Leads"
          value={o.leads?.new || 0}
          icon={Target}
          color="bg-indigo-600"
          trend={o.leads?.trend}
          sub="Pending review"
        />
        <KPICard
          label="Active Automations"
          value={o.automations?.active || 0}
          icon={Zap}
          color="bg-emerald-600"
          sub={`${o.automations?.runs30d || 0} runs · ${o.automations?.successRate || 0}% success`}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="card p-5">
          <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-violet-600" /> Strategic Insights
          </h3>
          <div className="space-y-3">
            {insights.map((insight, idx) => (
              <div key={`${insight.title}-${idx}`} className={`rounded-xl border p-3 ${severityStyle(insight.severity)}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide opacity-80">{severityLabel(insight.severity)}</p>
                    <p className="text-sm font-semibold mt-0.5">{insight.title}</p>
                    <p className="text-xs mt-1.5 opacity-90">{insight.detail}</p>
                  </div>
                  <button
                    onClick={() => navigate(normalizePath(insight.actionPath))}
                    className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-white/70 hover:bg-white transition-colors text-gray-700"
                  >
                    {insight.actionLabel}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card p-5">
          <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-green-600" /> Goal Tracking
          </h3>
          <div className="space-y-4">
            <GoalRow
              title="Monthly Publishing Target"
              current={o.content?.published || 0}
              target={Math.max(20, (o.content?.total || 0) || 20)}
              helper="Published content velocity"
            />
            <GoalRow
              title="Campaign Budget Discipline"
              current={100 - enterpriseMetrics.budgetUtilization}
              target={100}
              helper="Higher value means lower burn pressure"
            />
            <GoalRow
              title="Automation Reliability"
              current={o.automations?.successRate || 0}
              target={95}
              helper="Success rate threshold for enterprise stability"
            />
            <GoalRow
              title="Email Engagement Benchmark"
              current={Math.round(o.email?.avgOpenRate || 0)}
              target={25}
              helper="Open rate target for lifecycle programs"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="card lg:col-span-3">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-blue-600" /> Active Campaigns
            </h3>
            <button
              onClick={() => navigate("/campaigns")}
              className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
            >
              View All <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="divide-y divide-gray-50">
            {campaignsSummary?.length ? (
              campaignsSummary.map((campaign) => (
                <div key={campaign.id} className="px-6 py-4 hover:bg-gray-50/50 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2.5">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[campaign.status] || "bg-gray-100 text-gray-600"}`}>
                        {campaign.status}
                      </span>
                      <span className="font-medium text-sm text-gray-900">{campaign.name}</span>
                    </div>
                    <span className="text-xs text-gray-400">{campaign.type || "campaign"}</span>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                        <span>
                          Budget: {formatMoney(campaign.spent || 0)} / {formatMoney(campaign.budget || 0)}
                        </span>
                        <span>{campaign.budgetPct}%</span>
                      </div>
                      <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            campaign.budgetPct > 90 ? "bg-red-500" : campaign.budgetPct > 70 ? "bg-yellow-500" : "bg-purple-500"
                          }`}
                          style={{ width: `${Math.min(campaign.budgetPct, 100)}%` }}
                        />
                      </div>
                    </div>
                    {campaign.timelinePct > 0 ? (
                      <div className="text-right">
                        <div className="flex items-center gap-1 text-xs text-gray-400">
                          <Clock className="w-3 h-3" /> {campaign.timelinePct}%
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                    <span>{campaign._count?.contents || 0} content</span>
                    <span>{campaign._count?.scheduledPosts || 0} posts</span>
                    <span>{campaign._count?.milestones || 0} milestones</span>
                    {campaign.tags ? <span className="text-purple-400">#{campaign.tags.split(",")[0]}</span> : null}
                  </div>
                </div>
              ))
            ) : (
              <div className="px-6 py-12 text-center text-gray-400">
                <Megaphone className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No active campaigns</p>
                <button onClick={() => navigate("/campaigns")} className="mt-2 text-sm text-purple-600 hover:text-purple-700 font-medium">
                  Create your first campaign →
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="card lg:col-span-2">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Layers className="w-5 h-5 text-green-600" /> Content Pipeline
            </h3>
            <button
              onClick={() => navigate("/content")}
              className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
            >
              Studio <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="px-6 py-5">
            <div className="flex items-center justify-between gap-1">
              {pipelineData.map((stage, i) => (
                <div key={stage.stage} className="flex items-center gap-1 flex-1">
                  <div className="flex-1 text-center">
                    <div
                      className={`mx-auto w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold ${
                        i === 3
                          ? "bg-green-100 text-green-700"
                          : i === 0
                            ? "bg-gray-100 text-gray-600"
                            : i === 1
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-blue-100 text-blue-700"
                      }`}
                    >
                      {stage.count}
                    </div>
                    <p className="text-xs text-gray-500 mt-1.5 font-medium">{stage.stage}</p>
                  </div>
                  {i < pipelineData.length - 1 ? <ArrowRight className="w-4 h-4 text-gray-300 flex-shrink-0" /> : null}
                </div>
              ))}
            </div>
          </div>

          {contentTypeData.length > 0 ? (
            <div className="px-6 pb-4">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-3">By Type</p>
              <div className="space-y-2">
                {contentTypeData.map((typeItem) => (
                  <div key={typeItem.name} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 capitalize">{typeItem.name}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-purple-400 rounded-full"
                          style={{ width: `${Math.min(100, pct(typeItem.count, o.content?.total || 1))}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500 w-6 text-right">{typeItem.count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {contentPipeline?.recent?.length ? (
            <div className="border-t border-gray-100">
              <p className="px-6 pt-4 pb-2 text-xs font-semibold text-gray-500 uppercase">Recent</p>
              {contentPipeline.recent.slice(0, 4).map((content) => (
                <div key={content.id} className="px-6 py-2.5 flex items-center justify-between hover:bg-gray-50/50">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">{content.title}</p>
                    <p className="text-xs text-gray-400">
                      {content.type} · {content.createdBy?.name || "—"}
                    </p>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ${STATUS_COLORS[content.status] || "bg-gray-100 text-gray-600"}`}>
                    {content.status}
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card p-6 lg:col-span-2">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h3 className="text-lg font-semibold mb-1 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-purple-600" /> Performance ({performanceWindow} Days)
              </h3>
              <p className="text-xs text-gray-400">Impressions, clicks & conversions across all channels</p>
            </div>
            <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1">
              {[7, 30, 90].map((days) => (
                <button
                  key={days}
                  onClick={() => setPerformanceWindow(days as 7 | 30 | 90)}
                  className={`px-2.5 py-1 text-xs rounded-md font-medium transition-colors ${
                    performanceWindow === days ? "bg-white text-purple-700 shadow-sm" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {days}d
                </button>
              ))}
            </div>
          </div>

          {adChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={adChartData}>
                <defs>
                  <linearGradient id="grad1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="grad2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#EC4899" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#EC4899" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="grad3" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }} />
                <Area type="monotone" dataKey="impressions" stroke="#8B5CF6" fill="url(#grad1)" strokeWidth={2} />
                <Area type="monotone" dataKey="clicks" stroke="#EC4899" fill="url(#grad2)" strokeWidth={2} />
                <Area type="monotone" dataKey="conversions" stroke="#10B981" fill="url(#grad3)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-gray-400">
              <div className="text-center">
                <TrendingUp className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No performance data yet</p>
                <p className="text-xs mt-1">Start campaigns to see analytics here</p>
              </div>
            </div>
          )}
        </div>

        <div className="card p-6">
          <h3 className="text-lg font-semibold mb-1 flex items-center gap-2">
            <Globe className="w-5 h-5 text-pink-600" /> By Platform
          </h3>
          <p className="text-xs text-gray-400 mb-4">Activity distribution across platforms</p>

          {platformData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={platformData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={50} paddingAngle={3}>
                  {platformData.map((_, i) => (
                    <Cell key={`${i}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-gray-400 text-sm text-center">
              <div>
                <Share2 className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p>Connect platforms to see distribution</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="card lg:col-span-3">
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Activity className="w-5 h-5 text-violet-600" /> Recent Activity
            </h3>
          </div>
          <div className="divide-y divide-gray-50 max-h-[400px] overflow-y-auto">
            {activity?.items?.length ? (
              activity.items.slice(0, 15).map((item, i) => {
                const IconComp = MODULE_ICONS[item.module] || Circle;
                const colorClass = MODULE_COLORS[item.module] || "text-gray-600 bg-gray-50";
                return (
                  <div key={`${item.id}-${i}`} className="px-6 py-3 flex items-start gap-3 hover:bg-gray-50/50 transition-colors">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${colorClass}`}>
                      <IconComp className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{item.title}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{item.detail}</p>
                    </div>
                    <div className="flex flex-col items-end flex-shrink-0">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[item.status] || "bg-gray-100 text-gray-600"}`}>
                        {item.status}
                      </span>
                      <span className="text-xs text-gray-400 mt-1">
                        {new Date(item.timestamp).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                      </span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="px-6 py-12 text-center text-gray-400">
                <Activity className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No recent activity</p>
              </div>
            )}
          </div>
        </div>

        <div className="card lg:col-span-2">
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" /> Module Status
            </h3>
          </div>
          <div className="p-4 space-y-2">
            {normalizedModuleHealth.map((module) => {
              const isActive = module.status === "active" || module.status === "connected";
              return (
                <button
                  key={module.name}
                  onClick={() => navigate(module.path)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-xl hover:bg-gray-50 transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${isActive ? "bg-green-500" : "bg-gray-300"}`} />
                    <span className="text-sm font-medium text-gray-800">{module.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {module.count > 0 ? (
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{module.count}</span>
                    ) : null}
                    <span className={`text-xs font-medium capitalize ${isActive ? "text-green-600" : "text-gray-400"}`}>{module.status}</span>
                    <ArrowRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-purple-500 transition-colors" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="card p-5">
          <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
            <ShieldAlert className="w-5 h-5 text-amber-600" /> Risk Radar
          </h3>
          {riskCampaigns.length ? (
            <div className="space-y-2">
              {riskCampaigns.map((campaign) => (
                <div key={campaign.id} className="rounded-xl border border-amber-200 bg-amber-50 p-3 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-amber-800">{campaign.name}</p>
                    <p className="text-xs text-amber-700">Budget burn at {campaign.budgetPct}% · Timeline {campaign.timelinePct}%</p>
                  </div>
                  <button
                    onClick={() => navigate("/campaigns")}
                    className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-white text-amber-800 hover:bg-amber-100"
                  >
                    Investigate
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-700">
              No immediate campaign budget risks detected.
            </div>
          )}
        </div>

        <div className="card overflow-hidden">
          <div className={`${GRADIENT} px-6 py-4`}>
            <h3 className="text-lg font-semibold text-white">Quick Actions</h3>
            <p className="text-sm text-white/70 mt-0.5">Jump into your most common tasks</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y divide-gray-100">
            {[
              { label: "New Campaign", desc: "Plan & launch", icon: Megaphone, path: "/campaigns", color: "text-blue-600" },
              { label: "Create Content", desc: "Write & schedule", icon: FileText, path: "/content", color: "text-green-600" },
              { label: "Email Blast", desc: "Send to list", icon: Mail, path: "/email", color: "text-purple-600" },
              { label: "Run Ads", desc: "Launch campaign", icon: DollarSign, path: "/ads", color: "text-red-500" },
              { label: "Social Post", desc: "Publish now", icon: Share2, path: "/social", color: "text-pink-600" },
              { label: "SEO Audit", desc: "Check rankings", icon: Search, path: "/seo", color: "text-cyan-600" },
              { label: "View Analytics", desc: "Track metrics", icon: BarChart3, path: "/analytics", color: "text-indigo-600" },
              { label: "Automations", desc: "Set up flows", icon: Zap, path: "/automations", color: "text-amber-600" },
            ].map((action) => (
              <button
                key={action.label}
                onClick={() => navigate(action.path)}
                className="flex items-center gap-3 px-6 py-5 hover:bg-gray-50 transition-colors text-left group"
              >
                <div className={`w-10 h-10 rounded-xl bg-gray-50 group-hover:bg-white flex items-center justify-center ${action.color}`}>
                  <action.icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{action.label}</p>
                  <p className="text-xs text-gray-400">{action.desc}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
